// Deep-file import: the barrel drags selector modules into this load order,
// and those build reselect selectors at module scope.
// eslint-disable-next-line webflow/module-top-level-imports
import {selectLibraryPluginIds} from '@packages/systems/libraries/libraryPluginIds';
import {constant} from 'lodash';
import {
  getInnermostSymbolInstance,
  getPathToFocusedComponentRoot,
  visitNativeIdPathSymbolInstances,
} from '@packages/utilities/native-id-paths';
import {Err, Ok, Result, either} from '@packages/utilities/fp/result';
import type {DesignerStateType} from '@packages/systems/core/records/DesignerState';
import type {CollectionStateType} from '@packages/systems/core/records/CollectionStateType';
import type {UiNodeStateType} from '@packages/systems/core/records/UiNodeState';
import type {DragDropState} from '@packages/systems/designer/dnd';
import type {PageState} from '@packages/systems/core/records/PageRecord';
import type {CssGridUiContextShape as CssGridUiState} from '@packages/systems/grid';
import type {ExternalState} from '@packages/systems/designer/external';
import {
  type PageBuildModeValidationContext,
  canUseComponentInPageBuildMode,
} from '@packages/utilities/PageBuildUtils';
import {
  Component,
  Plugins,
  Expressions,
  ElementPreset,
  ExpressionPath,
  Types,
} from '@packages/systems/wfdl';
import {
  getChildren,
  findFurthestAncestor,
  reduceElementTree,
  findChild,
  getNativeIdForProjectedElement,
  isUserComponentInst,
  isComponentRoot,
  getPrev,
  getNext,
  updateOneOfComponents,
  addElementAtAnchor,
  removeElementById,
  findElement,
  getClosestRichTextElement,
  furthestTypographicElement,
  getBody,
  isChildrenBound,
  hasRequiredOnlyOnceInSite,
  hasOrphanRequiredOnlyOnceInElement,
  maybeGetParent,
  updateClearfixForElement,
  updateComponentRenderInComponentMap,
  getKeyPathToElement,
  getNativeNodeAndElementFromNativeId,
  getTargetElementOrInstance,
  deriveNativeIdFromPath,
  addElementAtAnchorInPath,
  getClosestSlotInfo,
  getSlotDefaultExpression,
  setSlotDefaultExpression,
  replaceElement,
  resetSlotPropertyIfEmpty,
  isComponentInst,
  isUserComponent,
  getSlotDefaultDropFieldId,
  clearGridItemStylesAllQueries,
  getCurrentComponentType,
} from '@packages/utilities/platform';
import {
  isTextHolder,
  isFormWrapper,
  isFormLabel,
  isListItem,
  isDropdownLink,
  isNavbarLink,
  isNavbarMenu,
  isSliderWrapperChild,
  isFormForm,
  isAnyTypeOfForm,
  isListElement,
  isDropdownWrapper,
  isNavbarWrapper,
  isSliderWrapper,
  isFormInnerElement,
  isGridRow,
  isTabsPane,
  isTabsLinkElement,
  isSliderMask,
  isBody,
  isSlot,
  isTabsWrapper,
  isTabsElement,
  isTabsWrapperChild,
  isTabsMenu,
  isRequiredOnlyOnceInSite,
  isRequiredOnlyOnceInElement,
  isPredefinedComponent,
  isFormInputWrapper,
  isFormSelect,
  isFormTextarea,
  isRichText,
  isComponentOnlyDropTarget,
} from '@packages/utilities/elementTypeUtils';
import {isStandard} from '@packages/utilities/platform-elements';
import {
  getIsComponentOnlyPageSlotDescendantFromPath,
  getIsRichTextDescendantFromPath,
  isMoveableInPageBuildMode,
} from '@packages/utilities/PageBuildUtils';
import {
  selectCurrentComponent,
  getElementComponentEntry,
  getLiveComponentMap,
  selectRootElementWithFocusedInstancesExpanded,
  selectCurrentComponentType,
  getPluginsFromState,
} from '@packages/utilities/platform-selectors';
import {
  tryValidateNavigatorMoveFastPath,
  validateChanges,
  slotRestrictionErrorMessage,
  isSlotRestrictionCandidate,
  ValidationContext as DomValidationContext,
} from '@packages/utilities/dom-validation';
import type {ErrorCode} from '@packages/systems/core/constants/ErrorCodes';
import * as DesignerErrorCodes from '@packages/systems/core/constants/ErrorCodes';
import {emptyArray, identity, okToOption} from '@packages/utilities/fp/utils';
import {
  getGuidNullable,
  type NativeId,
  convertElementIdToNativeId,
  getInstanceIndicesFromNativeId,
  generateInstanceNativeId,
} from '@packages/systems/core/utils/NativeIdUtils';
import {type Option, Some} from '@packages/utilities/fp/option';
import {weakMemo, singleMemo} from '@packages/utilities/memo';
import {isLeaf} from '@packages/systems/constraints';
import {
  type DropData,
  type DropBounds,
  detectHotzones,
  findActiveHotzone,
  processChild,
  getClosestChild,
  detectDropPosition,
  ChildWithDimensions,
} from '../DropPositionUtils';
import {
  importNodeAddedStylesFromElementPreset,
  updateElementAssetsFromPreset,
} from '@packages/shared/LegacyStyleSystem/utils/StyleBlockPresetUtils';
import {type PluginName} from '@packages/systems/wfdl-Qualified';
import {
  findSlotDefaultElement,
  type RelativePositionPlacementOptions,
  updateXscpStyleBlockIds,
} from '@packages/utilities/platform';
import {type StyleBlockStateType} from '@packages/shared/LegacyStyleSystem/states/StyleBlockStateTypes';
import type {IX2StoreState} from '@packages/systems/ix2/types-core';
import type {BetaFeatureStateType} from '@packages/systems/designer/beta-features';
import {placeGridItem} from '../../placeGridItem';

import {
  EElement,
  EList,
  type Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {designerTranslation} from '@packages/systems/designer-translations';
import {isElementGridDisplay} from '@packages/shared/utils/isNodeGridDisplay';
import {getPages} from '@packages/shared/utils/PageTreeUtils';
import {WebflowEnvironments} from '@packages/systems/core-constants';
import type {DesignerStores} from '@packages/systems/designer/flux';
import {
  applySlotDefaultInsertion,
  getComponentBySlotElementId,
  getComponentSlots,
  getSlotAllowedComponentIds,
  getSlotOverrideFieldId,
  getSlotRestrictions,
  isComponentAllowedInSlot,
  regenerateSlotOverrides,
  setSlotAllowedComponentIds,
} from '@packages/systems/component-slots';
import {getClosestOptimizationTreatment} from '@packages/systems/optimization/client';
import {
  validateElementAdd,
  validateElementMove,
} from '@packages/systems/element-move-validation';
import {isMovable, proxyElement} from '@packages/systems/element-move-utils';
// Reads only: the module's write half imports handleElementMoved from this file,
// so depending on its root here would be a cycle.
import * as SlotDefaults from '../SlotDefaults/reads';
export {postProcess} from './postProcess';
import {getFeatureFlagOutsideOfReact} from '@packages/systems/feature-config/client';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {isLocalDevCodeComponent} from '@packages/systems/code-libraries/designer';

/**
 * Traverses up the componentMap finding the furthest TextHolder element
 */
export const furthestTextHolderElement =
  (root: Expression) =>
  (element: ExpressionElement): ExpressionElement | undefined => {
    const leafElement = Expressions.getElementById(root, element.id);
    if (!leafElement) {
      return undefined;
    }

    return findFurthestAncestor(root, leafElement, isTextHolder);
  };

export const pickUp = ({
  designerState,
  uiNodeState,
  element,
  pageBuildModeProps,
}: {
  designerState: DesignerStateType;
  uiNodeState: UiNodeStateType;
  element: ExpressionElement;
  pageBuildModeProps?: {
    isPageBuildMode: boolean;
    nativeIdPath: NativeId[] | null;
  };
}): Result<ErrorCode, ExpressionElement> => {
  // this can be a page or a component root
  const root = selectCurrentComponent({
    DesignerStore: designerState,
    UiNodeStore: uiNodeState,
  });
  const plugins = getPluginsFromState({DesignerStore: designerState});

  const furthestElement =
    furthestTypographicElement(element, Component.getRender(root)) ?? element;

  const target = proxyElement(root)(plugins)(furthestElement);

  const {components} = designerState;

  if (pageBuildModeProps) {
    const {isPageBuildMode, nativeIdPath} = pageBuildModeProps;
    if (
      isPageBuildMode &&
      !isMoveableInPageBuildMode({
        nativeIdPath,
        componentMap: components,
        focusedSymbolNativeIdPath: uiNodeState.focusedSymbolNativeIdPath,
      })
    ) {
      return Err(DesignerErrorCodes.UnmovableElement(target));
    }
  }

  // Don't allow the element to be picked up if it can't be moved
  if (!isMovable(plugins, components, target)) {
    return Err(DesignerErrorCodes.UnmovableElement(target));
  }

  return Ok(target);
};

export const getPickUpElement = ({
  element,
  designerState,
  uiNodeState,
  pageBuildModeProps,
}: {
  element: ExpressionElement;
  designerState: DesignerStateType;
  uiNodeState: UiNodeStateType;
  pageBuildModeProps?: {
    isPageBuildMode: boolean;
    nativeIdPath: NativeId[] | null;
  };
}): ExpressionElement | null => {
  return either<ErrorCode, null | ExpressionElement>(() => null)(
    identity<null | ExpressionElement>
  )(
    pickUp({
      designerState,
      uiNodeState,
      element,
      pageBuildModeProps,
    }).map((el: ExpressionElement) =>
      // If moving tabs panes, drag the tabs content element instead
      isTabsPane(el) || isTabsLinkElement(el)
        ? Expressions.getParent(EElement(el), el.id)
        : el
    )
  );
};

export const containsFormInnerElement = (
  element: ExpressionElement
): boolean => {
  return Boolean(findChild(element, isFormInnerElement));
};

const getTypeCheck = (
  element: ExpressionElement
): ((arg1: ExpressionElement) => boolean) | null => {
  // If we're dropping a form label, it can be added to either a form element
  // or an add-to-cart form
  if (isFormLabel(element)) {
    return isAnyTypeOfForm;
  }

  // Form elements can only be dragged into the <form> tag inside a form wrapper
  // Check for `containsFormElement` to detect elements being dragged that contain a form element
  // and exclude form wrapper from that check (in order to not show the drop mask for form wrappers)
  if (containsFormInnerElement(element) && !isFormWrapper(element)) {
    return isFormForm;
  }

  // List items can only be dragged directly into a UL or OL list
  if (isListItem(element)) {
    return isListElement;
  }

  // dropdown links should only be able to move within their parents
  if (isDropdownLink(element)) {
    return isDropdownWrapper;
  }

  // Navbar components can't be moved outside of navbar wrappers
  if (isNavbarLink(element) || isNavbarMenu(element)) {
    return isNavbarWrapper;
  }

  // Slider inner components should stay inside of sliders
  if (isSliderWrapperChild(element)) {
    return isSliderWrapper;
  }

  return null;
};

/**
 * Returns compatible drop targets if dragging an element limited to compatible drop zones (like form elements)
 */
export const getDropTargets = (
  element: ExpressionElement,
  designerState: DesignerStateType,
  uiNodeState: UiNodeStateType
) => {
  const emptyDropTarget = {
    dropTargets: emptyArray,
    isDropLimited: false,
  } as const;
  const validate = getTypeCheck(element);

  if (validate != null) {
    // this can be a page or a component root
    const root = Expressions.getElement(
      Component.getRender(
        selectCurrentComponent({
          DesignerStore: designerState,
          UiNodeStore: uiNodeState,
        })
      )
    );

    return root
      ? {
          dropTargets: reduceElementTree(
            root,
            (acc, el) => {
              if (validate(el)) {
                // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'never'.
                acc.push(el.id);
              }
              return acc;
            },
            []
          ),
          isDropLimited: true,
        }
      : emptyDropTarget;
  }

  return emptyDropTarget;
};

type GetElementExprByIdOrInterimDragElementType = (
  arg1: Expression,
  arg2: DragDropState,
  arg3: string | null
) => Expression | undefined;

export const getElementExprByIdOrInterimDragElement: GetElementExprByIdOrInterimDragElementType =
  (expression, dragDropState, nativeId) => {
    const elementId = getGuidNullable(nativeId);
    if (elementId) {
      const element = Expressions.getElementById(expression, elementId);
      if (element != null) {
        return EElement(element);
      }
    }
    return dragDropState.interimDragElement?.dragElementExpr;
  };

/**
 * The element the render holds at this path, or null when it holds none — the
 * case for Slot default content, and for anything inside a component instance
 * that is not currently focused.
 */
const findRenderedElement = (
  root: Expression | null,
  nativeIdPath: NativeId[],
  focusedSymbolNativeIdPath: NativeId[]
): ExpressionElement | null => {
  if (!root) return null;

  const elementId = getGuidNullable(
    deriveNativeIdFromPath(nativeIdPath, focusedSymbolNativeIdPath)
  );
  if (!elementId) return null;

  return Expressions.getElementById(root, elementId);
};

/**
 * The element a drag/drop path points at.
 *
 * Two trees can answer. Prefer the render's copy: the drop pipeline reads the
 * anchor's parent out of `root`, so an element sourced only from the componentMap
 * has no parent there and a before/after drop dies on "Parent drop element is
 * missing". Slot defaults are never in the render, so they skip the lookup.
 */
export const resolveDragDropElement = ({
  root,
  nativeIdPath,
  focusedSymbolNativeIdPath,
  designerState,
}: {
  root: Expression | null;
  nativeIdPath: NativeId[];
  focusedSymbolNativeIdPath: NativeId[];
  designerState: DesignerStateType;
}): ExpressionElement | null => {
  if (
    !SlotDefaults.isSlotDefaultPath({
      designerState,
      uiNodeState: {focusedSymbolNativeIdPath},
      nativeIdPath,
    })
  ) {
    const renderedElement = findRenderedElement(
      root,
      nativeIdPath,
      focusedSymbolNativeIdPath
    );
    if (renderedElement) return renderedElement;
  }

  return getTargetElementOrInstance(
    nativeIdPath,
    getLiveComponentMap(designerState),
    designerState.plugins
  );
};

/**
 * Calculates an adjusted drop position based on the current mouse position inside the canvas.
 * Returns the proxied drop position e.g. { hoverNode: DomNode, position: 'before', dropMode: 'inline-block', hotzone: ... }
 * ------------------------
 * DROP RULES: ------------
 * ------------------------
 *
 * General algorithm:
 *
 * 1. If the mouse is currently in a "hotzone" on the hovered element (generally very near the inside edges):
 *   a. Determine the drop position based on the hotzone settings
 *   b. Return - don't process children elements.
 *
 * 2. If the mouse is not anywhere near the edges:
 *   a. Calculate the relative position of all of this element's children
 *   b. Find the closest edge (of any element), and insert relative to the element this edge belongs to
 *
 * Notes:
 * - Typographic elements can't have elements dragged inside of them, so they have hotzones extended over their entire surface area
 */
export const getDropPosition = ({
  designerState,
  uiNodeState,

  // The element and DOM node under the cursor. The caller has already resolved
  // both to decide whether the drag frame is worth processing at all, so it hands
  // them over rather than making us walk the tree for them a second time.
  hoverTarget,

  hoveredNodeBounds,

  // If true, the current hover node can't be dragged into.
  forbidInside,

  // The mouse X position from the mousemove event (typically e.pageX)
  pageX,

  // The mouse Y position from the mousemove event (typically e.pageY)
  pageY,

  // The amount (in pixels) that the document sending the event is offset from the left of the browser window.
  docLeft,

  // The amount (in pixels) that the document sending the event is offset from the top of the browser window.
  docTop,

  // The scrollLeft value of the document sending the mousemove event.
  docScrollLeft,

  // The scrollTop value of the document sending the mousemove event.
  docScrollTop,

  //  The callback for creating a debugger
  listener,

  // The frame to resolve native nodes within. Required on multi-frame
  // canvases (e.g. MCC) where the same native id exists in every frame.
  frameId,
}: {
  designerState: DesignerStateType;
  uiNodeState: UiNodeStateType;
  hoverTarget: {
    element: ExpressionElement;
    nativeNode: HTMLElement;
    nativeId: string;
  };
  hoveredNodeBounds: DropBounds;
  forbidInside: boolean;
  pageX: number;
  pageY: number;
  docLeft: number;
  docTop: number;
  docScrollLeft: number;
  docScrollTop: number;
  listener: any;
  frameId?: string | null;
}): DropData => {
  const root = selectRootElementWithFocusedInstancesExpanded({
    DesignerStore: designerState,
    UiNodeStore: uiNodeState,
  });

  const sharedNodeGetterParams = {
    focusedSymbolNativeIdPath: uiNodeState.focusedSymbolNativeIdPath,
    liveComponentMap: getLiveComponentMap(designerState),
    currentComponent: selectCurrentComponent({
      DesignerStore: designerState,
      UiNodeStore: uiNodeState,
    }),
    rootElementWithFocusedInstancesExpanded: root,
  } as const;

  // Start at the hover target and narrow to a closer child below if one wins.
  let dropElement: ExpressionElement = hoverTarget.element;
  let dropNode: HTMLElement = hoverTarget.nativeNode;

  // The mouse position relative to the top left corner of the element being hovered over
  const mX = Math.round(
    pageX - hoveredNodeBounds.left + docLeft - docScrollLeft
  );
  const mY = Math.round(pageY - hoveredNodeBounds.top + docTop - docScrollTop);

  // ----------------------------------------------------------------------------------
  // Step 1: Calculate the hotzones within the edges of the currently hovered element
  // ----------------------------------------------------------------------------------
  const hotzones = !isBody(dropElement)
    ? detectHotzones(hoveredNodeBounds, forbidInside)
    : emptyArray;

  // Find the first zone that the mouse overlaps
  const activeZone = findActiveHotzone(hotzones, mX, mY);

  // ----------------------------------------------------------------------------------
  // Step 2: If not inside a hotzone, calculate position relative to element's children
  // ----------------------------------------------------------------------------------
  let closestChild = null;
  let processedChildren: Array<ChildWithDimensions> | Array<never> = [];
  if (!activeZone && !forbidInside && root !== null) {
    const hoveredElementChildren = getChildren(dropElement) || [];
    processedChildren = hoveredElementChildren
      .map(({id}) => {
        const nativeId = getNativeIdForProjectedElement(
          root,
          id,
          hoverTarget.nativeId
        );
        let nativeNode = null;
        let element = null;
        if (nativeId) {
          const result = getNativeNodeAndElementFromNativeId({
            ...sharedNodeGetterParams,
            nativeNodeId: nativeId,
            frameId,
          });

          nativeNode = result.nativeNode;
          element = result.element;
        }

        return nativeNode && element ? {nativeNode, element} : null;
      })
      .filter(Boolean) // only process children with corresponding native nodes
      // @ts-expect-error - TS2339 - Property 'element' does not exist on type '{ nativeNode: HTMLElement; element: ElementF<any>; } | null'. | TS2339 - Property 'nativeNode' does not exist on type '{ nativeNode: HTMLElement; element: ElementF<any>; } | null'.
      .map(({element, nativeNode}) =>
        processChild(
          element,
          nativeNode,
          hoveredNodeBounds,
          mX,
          mY,
          docLeft,
          docTop
        )
      );

    // Switch context to use the child as the hover target instead of the original node
    closestChild = getClosestChild(processedChildren, mX, mY);

    if (closestChild) {
      dropElement = closestChild.element;
      dropNode = closestChild.node;
    }
  }

  // If we're not in a hotzone or in child detection mode, force the position to append
  const forcePosition = activeZone || closestChild ? null : 'append';

  // ----------------------------------------------------------------------------------
  // Step 3: Analyze target drop position/mode (e.g. block, inline-block, float-*, etc)
  // ----------------------------------------------------------------------------------
  const positionDetails = detectDropPosition(
    dropNode,
    hoveredNodeBounds,
    mX,
    mY,
    docLeft,
    docTop,
    forcePosition
  );

  // Dispatch data required for a debug view
  if (listener) {
    listener({
      bounds: hoveredNodeBounds,
      mouse: {x: mX, y: mY, label: positionDetails.dropMode},
      childNodes: processedChildren,
      hotzones,
    });
  }

  return {
    hoverElement: dropElement,
    hoverNode: dropNode,
    position: positionDetails.position,
    dropMode: positionDetails.dropMode,
    hotzone: activeZone || null,
    mouse: {
      x: mX,
      y: mY,
    },
  };
};

/**
 * Performs preprocessing tasks before a drop location is determined.
 * returns {
    hoverElement: The proxied hover element,
    forbidInside: True if the hover element can't contain elements
  }
 */
export const preProcess = (
  designerState: DesignerStateType,
  uiNodeState: UiNodeStateType,
  dragElement: ExpressionElement | null,
  hoveredElement: ExpressionElement,
  nativeIdPaths?: {
    dragNativeIdPath?: NativeId[] | null;
    hoveredNativeIdPath?: NativeId[] | null;
  }
) => {
  const plugins = getPluginsFromState({DesignerStore: designerState});
  const components = getLiveComponentMap(designerState);

  if (!dragElement) {
    console.warn(
      'ThisIsNotABug: dragElementIsMissingInPreProcess',
      'Drag attempted but the drag element did not exist'
    );
    return {
      hoveredElement,
      forbidInside:
        // Ensure `forbidInside` returns `false` for symbol instances. This will allow attempting a drag into
        // symbol instances rather than wrapping around them, and therefore running the drag position validation.
        !isUserComponentInst(components, hoveredElement) ||
        isLeaf(Plugins.getAtom(plugins, hoveredElement), plugins) ||
        isChildrenBound(hoveredElement),
    };
  }

  const root = Component.getRender(
    selectCurrentComponent({
      DesignerStore: designerState,
      UiNodeStore: uiNodeState,
    })
  );
  const isNativeIdPathPrefix = (
    prefix: NativeId[] | null | undefined,
    path: NativeId[] | null | undefined
  ): boolean => {
    if (!prefix || !path || prefix.length >= path.length) return false;
    return prefix.every((nativeId, index) => nativeId === path[index]);
  };
  const dragNativeIdPath = nativeIdPaths?.dragNativeIdPath;
  const hoveredNativeIdPath = nativeIdPaths?.hoveredNativeIdPath;
  // Determine if the currently dragged element is being dragged over itself or one of it's descendants
  const isDirect =
    Object.is(hoveredElement, dragElement) ||
    Boolean(
      dragNativeIdPath &&
      hoveredNativeIdPath &&
      dragNativeIdPath.length === hoveredNativeIdPath.length &&
      dragNativeIdPath.every(
        (nativeId, index) => nativeId === hoveredNativeIdPath[index]
      )
    );
  const parentElement = Expressions.getParent(root, hoveredElement.id);
  const isElementAncestor =
    !isDirect &&
    (isNativeIdPathPrefix(dragNativeIdPath, hoveredNativeIdPath) ||
      Boolean(
        parentElement &&
        Expressions.closestElement(root, parentElement.id, (element) =>
          Object.is(element, dragElement)
        )
      ));

  const overSelf = isDirect || isElementAncestor;

  // If we're hovering over an ancestor of the element being dragged, switch the context to pretend
  // that the hover event originated from the element being dragged.
  if (isElementAncestor) {
    hoveredElement = dragElement;
  }

  // A Slot in its own definition is a valid drop target (authors its default);
  // mirror validateNesting so targeting offers an inside position, not a sibling.
  // Lazy: preProcess runs per drag mousemove; only slot hovers pay this cost.
  const canAuthorSlotDefault = () => {
    if (!getFeatureFlagOutsideOfReact(FEATURE_FLAGS.COMPONENT_SLOT_DEFAULTS)) {
      return false;
    }
    const currentComponentType = selectCurrentComponentType({
      DesignerStore: designerState,
      UiNodeStore: uiNodeState,
    });
    if (!isUserComponent(currentComponentType)) return false;

    // The default can only be stored on defaultData, so don't offer the drop
    // until the component has been migrated onto it.
    const currentComponent = components.get(currentComponentType);
    return Boolean(
      currentComponent && Component.getDefaultData(currentComponent)
    );
  };

  // Lazy for the same reason as canAuthorSlotDefault: only slot hovers pay it.
  const canDropIntoSlotDefault = () =>
    SlotDefaults.canDropIntoSlotDefault({
      designerState,
      uiNodeState,
      hoveredElement,
      hoveredNativeIdPath,
    });

  /**
   * Don't allow dropping into:
   * - A element into itself
   * - Any elements into closed elements (textarea, br, hr)
   * - <select> elements (Webflow manages inner options manually)
   * - Typographic, Richtext, or Content Elements (Edited with RTE)
   * - A component instance (that component needs to be focused first)
   * - Components: embeds, videos, maps, navbar wrappers, slider wrappers
   */
  const forbidInside =
    overSelf ||
    // Ensure `forbidInside` is set to `false` for symbol instances. This will allow attempting a drag into
    // symbol instances rather than wraping around them, and therefore running the drag position validation.
    !isStandard(hoveredElement, components) ||
    isLeaf(Plugins.getAtom(plugins, hoveredElement), plugins) ||
    (isChildrenBound(hoveredElement) &&
      !(
        isSlot(hoveredElement) &&
        (canAuthorSlotDefault() || canDropIntoSlotDefault())
      )) ||
    Boolean(
      // Special processing for tabs
      isTabsElement(dragElement) &&
      // Forbid dragging tabs elements into other elements (except the main tabs element)
      !isTabsWrapper(dragElement)
    );

  // Special processing for tabs
  if (isTabsLinkElement(dragElement)) {
    // Tabs links - prevent dragging into each other, keep links within menu
    // If the hovered element is inside a tab link...
    const hoveredTabLink =
      hoveredElement &&
      Expressions.closestElement(root, hoveredElement.id, isTabsLinkElement);

    // Reassign the hovered element to the hovered tab link
    if (hoveredTabLink) {
      hoveredElement = hoveredTabLink;
    } else {
      // Otherwise, keep hover contained to tabs link menu
      const closestEl = Expressions.closestElement(
        root,
        dragElement.id,
        isTabsMenu
      );
      if (closestEl) {
        hoveredElement = closestEl;
      }
    }
  }

  if (isTabsWrapperChild(dragElement)) {
    // Tabs content & menu - keep within main tabs element
    const closestEl = Expressions.closestElement(
      root,
      dragElement.id,
      isTabsWrapper
    );
    if (closestEl) {
      hoveredElement = closestEl;
    }
  }

  return {
    hoveredElement,
    forbidInside,
  };
};

export const getAnchorForGridItems = ({
  dragElement,
  dropParent,
  designerState,
  uiNodeState,
  isNewElement,
}: {
  dragElement: ExpressionElement;
  dropParent: ExpressionElement;
  designerState: DesignerStateType;
  uiNodeState: UiNodeStateType;
  isNewElement: boolean;
}): Option<{
  anchorElement: ExpressionElement;
  anchorPosition: RelativePositionPlacementOptions;
}> => {
  const root = Component.getRender(
    selectCurrentComponent({
      DesignerStore: designerState,
      UiNodeStore: uiNodeState,
    })
  );
  /*
   * Handles when a new element is being added into a grid container, or if the
   * user is moving an existing element from another parent into a grid container.
   * This ensures that the element is appended as the last child of the the grid container
   * element so that DOM order is based on when the node was moved or added inside.
   */
  if (
    isNewElement ||
    Expressions.getParent(root, dragElement.id) !== dropParent
  ) {
    return Some<{
      anchorElement: ExpressionElement;
      anchorPosition: RelativePositionPlacementOptions;
    }>({
      anchorElement: dropParent,
      anchorPosition: 'append',
    });
  }

  /*
   * Handles when an existing element is just moving to a different cell/area inside
   * the same parent grid container. We maintain its anchor position by determining
   * its sibling — if it's after the sibling, then position it after and vice versa
   */
  // @ts-expect-error - TS2322 - Type 'Option<{ anchorElement: ElementF<any>; anchorPosition: string; }>' is not assignable to type 'Option<{ anchorElement: ElementF<any>; anchorPosition: RelativePositionPlacementOptions; }>'.
  return getPrev(dragElement.id)(EElement(dropParent))
    .map((siblingElement: ExpressionElement) => ({
      anchorElement: siblingElement,
      anchorPosition: 'after',
    }))
    .alt(
      getNext(dragElement.id)(EElement(dropParent)).map(
        (siblingElement: ExpressionElement) => ({
          anchorElement: siblingElement,
          anchorPosition: 'before',
        })
      )
    );
};

// Used for creating possible components structure that will be the result of the drag and drop action
export const getStagedComponents = weakMemo((childElement: ExpressionElement) =>
  weakMemo((anchorElement: ExpressionElement) =>
    singleMemo((anchorPosition: RelativePositionPlacementOptions) =>
      weakMemo((components: Component.ComponentMap) =>
        weakMemo((liveExpressionTree: ExpressionElement) =>
          weakMemo((anchorPath: ExpressionPath) =>
            singleMemo((isNewElement: boolean) => {
              if (!isNewElement && anchorElement === childElement)
                return components;

              if (!isNewElement) {
                components = updateOneOfComponents(
                  childElement,
                  liveExpressionTree,
                  components,
                  (renderExpr) => removeElementById(renderExpr, childElement.id)
                );
              }

              return updateOneOfComponents(
                anchorElement,
                liveExpressionTree,
                components,
                (renderExpr) =>
                  addElementAtAnchorInPath(
                    renderExpr,
                    childElement,
                    anchorElement,
                    anchorPosition,
                    anchorPath.length > 0 ? anchorPath : null,
                    components
                  )
              );
            })
          )
        )
      )
    )
  )
);

export const isElementPartOfFocusedComponent = (
  focusedSymbolNativeIdPath: NativeId[],
  nativeId: NativeId,
  designerStore: DesignerStateType
) => {
  if (!focusedSymbolNativeIdPath.length) return false;

  // if the clicked element is not inside the focused component root then we unfocus the component
  return (
    getElementFocusedParentComponents(
      focusedSymbolNativeIdPath,
      nativeId,
      designerStore
    ).length === focusedSymbolNativeIdPath.length
  );
};

export const getElementFocusedParentComponents = (
  focusedSymbolNativeIdPath: NativeId[],
  nativeId: NativeId | string | null,
  designerStore: DesignerStateType
): NativeId[] => {
  const components = getLiveComponentMap(designerStore);
  const body = getBody(components);
  const clickedElementId = getGuidNullable(nativeId);
  if (!body || !clickedElementId) return [];

  // An instance authored as another component's Slot default lives on that
  // component's `defaultData`, not in any render, so walking renders alone
  // loses the path. The shared walk reads both.
  let elementFoundAtIndex = -1;
  visitNativeIdPathSymbolInstances(
    // The walk visits every segment but the last, so the clicked element is
    // appended to keep the innermost focused instance in the visit. Its value
    // is never read.
    [
      ...focusedSymbolNativeIdPath,
      convertElementIdToNativeId(clickedElementId),
    ],
    body,
    components,
    (focusedComponentInstance, focusedComponentRoot) => {
      // The walk skips segments it cannot resolve, so counting visits would
      // drift from the path the result is sliced by.
      const focusedComponentIndex = focusedSymbolNativeIdPath.findIndex(
        (focusedNativeId) =>
          getGuidNullable(focusedNativeId) === focusedComponentInstance.id
      );
      if (focusedComponentIndex === -1) return;

      const clickedElementIsSlotDefault =
        findSlotDefaultElement(
          designerStore.plugins,
          components,
          focusedComponentInstance.type,
          clickedElementId
        ) !== null;

      // if the clicked element is not inside the focused component root then we unfocus the component
      if (
        clickedElementIsSlotDefault ||
        Boolean(
          Expressions.getElementById(
            EElement(focusedComponentRoot),
            clickedElementId
          )
        ) ||
        findElement(focusedComponentRoot, (e) => e.id === clickedElementId)
      ) {
        elementFoundAtIndex = focusedComponentIndex;
      }
    },
    designerStore.plugins
  );

  return elementFoundAtIndex !== -1
    ? focusedSymbolNativeIdPath.slice(0, elementFoundAtIndex + 1)
    : [];
};

// A fresh root path per call would miss the walker's memo on every drag frame.
const getFocusedComponentRootPath = weakMemo(
  (focusedComponentNativeIdPath: NativeId[]) =>
    weakMemo((components: Component.ComponentMap) =>
      getPathToFocusedComponentRoot(focusedComponentNativeIdPath, components)
    )
);

export const getFocusedComponentInstElement = (
  focusedComponentNativeIdPath: NativeId[],
  designerState: DesignerStateType
) => {
  const components = getLiveComponentMap(designerState);
  const focusedRootPath = getFocusedComponentRootPath(
    focusedComponentNativeIdPath
  )(components);
  if (!focusedRootPath) return undefined;

  return (
    getInnermostSymbolInstance(
      focusedRootPath,
      components,
      designerState.plugins
    ) ?? undefined
  );
};

/**
 * Determines if an element's drop position needs to be proxied to a different element.
 * For example, if an element is targeted to go *inside* of a grid row, it will proxy the placement to go *after* the grid row instead.
 * @param  {ExpressionElement}   anchor       The ExpresionElement selected in the designer.
 * @param  {TreeRootNode}           root         The Expression tree root
 * @param  {DesignerStateType}      designerState
 * @param  {Boolean}                forceAfter   (Optional) If set to true and no specific proxy rules match, will default to adding *after* the target element.
 * @param  {Boolean}                skipRichTextProxy (Optional) If set to true, rich text anchor proxying is skipped.
 * @param  {ExpressionElement}      insertedElement (Optional) The new element being inserted.
 * @return {ProxyInfo}                            Where the element should actually be appended. In { anchorNode: ExpressionElement, position: 'after|before|append' } format. Null if the paste/drop is not allowed.
 */
export const proxyAnchor = (
  anchor: ExpressionElement,
  root: Expression,
  designerState: DesignerStateType,
  forceAfter: boolean = false,
  skipRichTextProxy: boolean = false,
  insertedElement?: ExpressionElement
): {
  anchorNode: ExpressionElement;
  position: RelativePositionPlacementOptions;
} => {
  if (!anchor) {
    throw new Error('Must supply an anchor node to test for proxying!');
  }

  const proxyInfoAfter /* ProxyInfo */ = {
    anchorNode: anchor,
    position: 'after',
  } as const;

  const componentMap = getLiveComponentMap(designerState);
  // If pasting into a component instance, always add after instead of inside since the instance is just a placeholder
  if (isUserComponentInst(componentMap, anchor)) {
    return proxyInfoAfter;
  }

  // If pasting anywhere inside a rich text element, paste after
  const richTextAncestor = getClosestRichTextElement(root, anchor.id);
  if (richTextAncestor && !skipRichTextProxy) {
    return {
      anchorNode: richTextAncestor,
      position: 'after',
    } /* ProxyInfo */;
  }

  // If pasting into a micro element, paste after it's typographic parent instead
  const typographicAncestor = furthestTypographicElement(anchor, root);
  if (typographicAncestor) {
    return {
      anchorNode: typographicAncestor,
      position: 'after',
    } /* ProxyInfo */;
  }

  // For typographic elements, paste after the target (since we can't paste inside)
  if (isTextHolder(anchor)) {
    return proxyInfoAfter;
  }

  // For components (widgets, navbars, sliders, embeds), do the same
  // We don't do the logic specifically for slider masks, so you can paste slides into them
  if (isPredefinedComponent(anchor) && !isSliderMask(anchor)) {
    return proxyInfoAfter;
  }

  // Don't allow pasting inside of a checkbox or radio button wrapper
  if (isFormInputWrapper(anchor)) {
    return proxyInfoAfter;
  }

  // Don't allow pasting inside of a form <select> element
  if (isFormSelect(anchor)) {
    return proxyInfoAfter;
  }

  // Don't allow pasting inside of a form <textarea> element
  if (isFormTextarea(anchor)) {
    return proxyInfoAfter;
  }

  if (
    isFormWrapper(anchor) &&
    insertedElement &&
    !isFormWrapper(insertedElement) &&
    (isFormInnerElement(insertedElement) ||
      containsFormInnerElement(insertedElement))
  ) {
    const form = findChild(anchor, isFormForm);
    if (form) {
      return {
        anchorNode: form,
        position: 'append',
      };
    }
  }

  // Grid rows and form wrappers not handled above place the element after themselves
  if (isGridRow(anchor) || isFormWrapper(anchor)) {
    return proxyInfoAfter;
  }

  // If the caller wants to force placement after the target element
  if (forceAfter) {
    return proxyInfoAfter;
  }

  // if element doesn't accept children paste after the target
  if (
    isLeaf(
      Plugins.getAtom(designerState.plugins, anchor),
      designerState.plugins
    )
  ) {
    return proxyInfoAfter;
  }

  // Default to 'append'
  return {
    anchorNode: anchor,
    position: 'append',
  };
};

export type ValidationContext = {
  designerState: DesignerStateType;
  validationRoot?: Expression;
  stagedComponents?: Component.ComponentMap | null;
  /**
   * The tree being validated is an ephemeral promoted fork, not the live one.
   * The Navigator fast path reads the raw live tree, so it cannot answer for a
   * placement whose graph only exists in that fork.
   */
  usesProjectedTree?: boolean;
  pageState: PageState;
  collectionState: CollectionStateType;
  ix2State: IX2StoreState;
  betaFeatureState: BetaFeatureStateType;
  uiNodeState: UiNodeStateType;
  isInOptimizeMode: boolean;
  canAddComponentsInStaticRichText: boolean;
  pageBuildModeValidationContext: PageBuildModeValidationContext;
  cmsRtfComponentIds?: readonly string[];
  // Fires only when a slot restriction actually blocks this placement --
  // omit it at hover-only call sites so cursor-feedback validation doesn't
  // also report an attempt.
  onSlotRestrictionBlocked?: (ctx: {
    parentId: string;
    slotId: string;
    attemptedComponentId: string;
  }) => void;
  placementDetails: {
    anchorElement: ExpressionElement;
    isClonedNode: boolean;
    dragElement: ExpressionElement;
    isNewNode: boolean;
    anchorPosition: RelativePositionPlacementOptions;
    anchorPath: ExpressionPath | null;
    useNavigatorMoveFastPath?: boolean;
    // Placement uses anchorPath; slot-restriction validation uses this, which
    // also resolves the slot when the anchor is content already sitting in one.
    // Required, not optional: a caller that omits it silently degrades to the
    // fail-closed branch, so a missed call site should be a compile error.
    validationAnchorPath: ExpressionPath | null;
  };
};

/**
 * The component whose render actually owns the anchor, falling back to the
 * focused one.
 *
 * Validation already accepts anchors anywhere on the focused path, so the
 * mutation needs to target whichever render actually owns the anchor id.
 */
export const resolveAnchorOwner = ({
  componentMap,
  designerState,
  uiNodeState,
  anchorId,
}: {
  componentMap: Component.ComponentMap;
  designerState: DesignerStateType;
  uiNodeState: UiNodeStateType;
  anchorId: string;
}): {
  component: Component.ComponentType;
  componentType: Plugins.QualifiedElement;
  render: Expression;
} => {
  const entry = getElementComponentEntry(
    componentMap,
    uiNodeState.focusedSymbolNativeIdPath,
    anchorId
  );
  const currentComponent = selectCurrentComponent({
    DesignerStore: designerState,
    UiNodeStore: uiNodeState,
  });
  return {
    component: entry?.component || currentComponent,
    componentType:
      entry?.componentType ||
      selectCurrentComponentType({
        DesignerStore: designerState,
        UiNodeStore: uiNodeState,
      }),
    render: entry?.render || Component.getRender(currentComponent),
  };
};

export const validateDragPlacement =
  (context: ValidationContext) =>
  (rawChild: ExpressionElement): Result<string, ExpressionElement> => {
    const {
      pageState,
      collectionState,
      designerState,
      uiNodeState,
      isInOptimizeMode,
      canAddComponentsInStaticRichText,
      pageBuildModeValidationContext,
    } = context;

    const {components} = designerState;

    const {
      anchorPosition,
      isNewNode,
      isClonedNode,
      dragElement,
      anchorPath,
      useNavigatorMoveFastPath,
      validationAnchorPath,
    } = context.placementDetails;

    const {anchorElement} = context.placementDetails;

    const {t} = designerTranslation;

    const root =
      context.validationRoot ??
      selectRootElementWithFocusedInstancesExpanded({
        DesignerStore: designerState,
        UiNodeStore: uiNodeState,
      });

    const currentComponent = selectCurrentComponent({
      DesignerStore: designerState,
      UiNodeStore: uiNodeState,
    });

    if (
      isInOptimizeMode &&
      currentComponent &&
      !isNewNode &&
      !getClosestOptimizationTreatment(
        anchorElement.id,
        currentComponent.render
      )
    ) {
      return Err(t('designer.components.optimization.drag-drop-error'));
    }

    if (pageBuildModeValidationContext?.isInPageBuildMode) {
      const isComponentInstance = isComponentInst(components)(dragElement);
      if (isComponentInstance) {
        if (
          !canUseComponentInPageBuildMode({element: dragElement, components})
        ) {
          const errorMessage =
            pageBuildModeValidationContext.canAccessDesignSiteMode
              ? t(
                  'designer.components.libraries.component-disabled-when-has-permission'
                )
              : t(
                  'designer.components.libraries.component-disabled-when-does-not-have-permission'
                );

          return Err<string>(errorMessage);
        }
      }
    }

    // A local-dev code component has no synced representation other
    // collaborators can resolve, so it can never be placed on a page directly
    // — only an already-installed real component's own instances render its
    // bundle, through a registered override.
    const draggedComponent = components.get(dragElement.type);
    if (
      isLocalDevCodeComponent(
        draggedComponent,
        dragElement.type,
        designerState.libraries,
        getFeatureFlagOutsideOfReact(FEATURE_FLAGS.LOCAL_CODE_LIBRARY_DEV)
      )
    ) {
      return Err<string>(
        t('designer.components.libraries.local-dev-cannot-be-placed')
      );
    }

    if (isUserComponentInst(components, anchorElement)) {
      /*
        Anchor position `append` or `prepend` with no anchor path present
        implies that we are trying to drag into a component instance,
        which is not allowed.

        Anchor position `after` or `before` with anchor path present also
        implies that we are trying to drag into a component instance.
      */
      if (
        anchorPath
          ? anchorPosition === 'after' || anchorPosition === 'before'
          : anchorPosition === 'append' || anchorPosition === 'prepend'
      ) {
        return Err<string>(t('designer.components.drag-drop-error'));
      }
    }
    const liveComponentMap = getLiveComponentMap(designerState);

    if (
      isComponentRoot(liveComponentMap)(anchorElement) &&
      (anchorPosition === 'before' || anchorPosition === 'after')
    ) {
      return Err<string>(
        'Elements inside a Component can only be moved within the same Component'
      );
    }

    if (isClonedNode && dragElement) {
      if (isRequiredOnlyOnceInSite(dragElement)) {
        return DesignerErrorCodes.errorCodeToMessage(
          DesignerErrorCodes.getCopyMessage
        )(Err(DesignerErrorCodes.OneInstanceRequired));
      }

      if (isRequiredOnlyOnceInElement(dragElement)) {
        return DesignerErrorCodes.errorCodeToMessage(
          DesignerErrorCodes.getCopyMessage
        )(Err(DesignerErrorCodes.OneInstancePerComponent));
      }

      if (
        hasRequiredOnlyOnceInSite(dragElement) ||
        hasOrphanRequiredOnlyOnceInElement(dragElement)
      ) {
        return DesignerErrorCodes.errorCodeToMessage(
          DesignerErrorCodes.getCopyMessage
        )(Err(DesignerErrorCodes.ContainsRequiredElement));
      }
    }

    // to make flow happy
    if (root === null) {
      return Err<string>('Root element is missing');
    }

    const rootElement = Expressions.getElement(root);

    // to make flow happy
    if (rootElement === null) {
      return Err<string>('Root element is missing');
    }

    const rawParent =
      anchorPosition === 'after' || anchorPosition === 'before'
        ? Expressions.getParent(root, anchorElement.id)
        : anchorElement;

    if (!rawParent) {
      return Err<string>('Parent element is missing');
    }

    const anchorIsInRichText = Boolean(
      // is within rich text
      (!isRichText(anchorElement) &&
        getClosestRichTextElement(root, anchorElement.id)) ||
      // anchor is rich text and is within it (e.g. not before or after)
      (isRichText(anchorElement) &&
        anchorPosition !== 'before' &&
        anchorPosition !== 'after')
    );

    const draggedFromRichText = Boolean(
      !isRichText(dragElement) &&
      getClosestRichTextElement(root, dragElement.id)
    );

    if (anchorIsInRichText) {
      const canDragComponentIntoRichText =
        canAddComponentsInStaticRichText &&
        // isNewNode is only true when adding and not when dragging from another location
        isNewNode &&
        // are we dragging a component instance?
        isComponentInst(liveComponentMap)(dragElement);

      if (!canDragComponentIntoRichText && !draggedFromRichText) {
        // an element from outside a rich text element should not be able to be dragged within it,
        // unless it is coming from the symbols panel, is a component, and canAddComponentsInStaticRichText is true
        return Err<string>('Cannot move elements into a rich text element');
      }
    }

    // an element from inside a rich text element should not be able to be dragged outside it.
    if (draggedFromRichText && !anchorIsInRichText) {
      return Err<string>('Cannot move elements out of a rich text element');
    }

    const liveComponents = getLiveComponentMap(designerState);
    const anchorOwner = resolveAnchorOwner({
      componentMap: liveComponents,
      designerState,
      uiNodeState,
      anchorId: anchorElement.id,
    });
    const slotDefaultFieldId = resolveSlotDefaultDropFieldId({
      component: anchorOwner.component,
      componentType: anchorOwner.componentType,
      anchorId: anchorElement.id,
      position: anchorPosition,
      anchorPath: anchorPath ?? null,
      componentMap: liveComponents,
      plugins: designerState.plugins,
    });

    const pages = getPages(pageState);
    const {focusedSymbolNativeIdPath} = uiNodeState;
    const isInPageBuildMode = pageBuildModeValidationContext.isInPageBuildMode;
    const validationContext: DomValidationContext = {
      collections: collectionState.collections,
      componentInstanceCounts: designerState.componentInstanceCounts,
      currentComponentType: getCurrentComponentType(
        liveComponentMap,
        focusedSymbolNativeIdPath
      ),
      currentPageId: designerState.currentPageId,
      plugins: designerState.plugins,
      userComponentUsageMap: designerState.userComponentUsageMap,
      cmsRtfComponentIds:
        context.cmsRtfComponentIds ?? designerState.cmsRtfComponentIds,
      pages,
      rawParent,
      rawChild: isClonedNode ? dragElement : rawChild,
      anchorPath: validationAnchorPath ?? anchorPath,
      isNewElement: isNewNode,
      onSlotRestrictionBlocked: context.onSlotRestrictionBlocked,
      runtimeArgs: {
        runtimeEnvironment: WebflowEnvironments.DESIGNER,
        isInPageBuildMode,
        isInCampaignBuildMode:
          pageBuildModeValidationContext.isInCampaignBuildMode,
        staleComponents: designerState.staleComponents,
        canAddComponentsInStaticRichText,
      },
      featureFlags: {
        isComponentSlotDefaultsEnabled: getFeatureFlagOutsideOfReact(
          FEATURE_FLAGS.COMPONENT_SLOT_DEFAULTS
        ),
      },
      libraryPluginIds: selectLibraryPluginIds(designerState.libraries),
    };

    if (
      useNavigatorMoveFastPath &&
      !isNewNode &&
      !isClonedNode &&
      !isInOptimizeMode &&
      !context.usesProjectedTree &&
      anchorPath === null &&
      slotDefaultFieldId === null
    ) {
      const fastPathResult = tryValidateNavigatorMoveFastPath(
        liveComponentMap,
        validationContext,
        {anchorElement, anchorPosition}
      );
      if (fastPathResult) {
        return fastPathResult.map(constant(rawChild));
      }
    }

    // Build mode only: the restriction constrains Marketers, and an author
    // working on the component in Design mode is the one who sets it.
    //
    // Shared with validateSlotRestrictions so the drag cursor and the drop
    // agree on what a candidate is. Evaluated inside the condition rather
    // than above it: this runs on every drag-over tick, and the component
    // lookups are not free.
    if (
      pageBuildModeValidationContext?.isInPageBuildMode &&
      isSlotRestrictionCandidate(
        liveComponents,
        designerState.plugins,
        rawChild,
        validationContext.libraryPluginIds
      )
    ) {
      let restrictionFieldId: string | null = null;
      let restrictionHost: Component.ComponentType | null = null;

      if (slotDefaultFieldId) {
        restrictionFieldId = slotDefaultFieldId;
        restrictionHost = anchorOwner.component;
      } else if (isSlot(rawParent)) {
        restrictionFieldId = getSlotOverrideFieldId(rawParent);
        // Resolve the owner from the Slot itself, the way
        // validateSlotRestrictions does. Walking up from the current render
        // finds nothing when the Slot lives in an instance's definition, and
        // this early signal would silently no-op -- the drop still gets blocked
        // later, but the cursor would not say so during the drag.
        restrictionHost =
          getComponentBySlotElementId(liveComponents).get(rawParent.id) ??
          anchorOwner.component;
      } else if (
        validationAnchorPath &&
        isUserComponentInst(liveComponents, rawParent)
      ) {
        const firstStep = validationAnchorPath[0];
        if (firstStep && firstStep.in === 'Record') {
          restrictionFieldId = firstStep.at;
          restrictionHost = liveComponents.get(rawParent.type) ?? null;
        }
      }

      if (restrictionFieldId && restrictionHost) {
        const restrictions = getSlotRestrictions(
          restrictionHost,
          restrictionFieldId,
          liveComponents
        );
        if (restrictions) {
          const dragComponentId = `${rawChild.type[0]}::${rawChild.type[1]}`;
          if (
            !isComponentAllowedInSlot(
              restrictions,
              dragComponentId,
              liveComponents
            )
          ) {
            // Absent from the live map before materialization, so fall back to
            // the plugin definition or the toast names no component at all.
            const dragComp =
              liveComponents.get(rawChild.type) ??
              Plugins.getPluginComponent(designerState.plugins, rawChild);
            let restrictionHostType: Plugins.QualifiedElement | undefined;
            liveComponents.find((component, type) => {
              if (component !== restrictionHost) return false;
              restrictionHostType = type;
              return true;
            });
            if (context.onSlotRestrictionBlocked && restrictionHostType) {
              context.onSlotRestrictionBlocked({
                parentId: `${restrictionHostType[0]}::${restrictionHostType[1]}`,
                slotId: restrictionFieldId,
                attemptedComponentId: dragComponentId,
              });
            }
            return Err<string>(
              slotRestrictionErrorMessage(
                dragComp ? Component.getDisplayName(dragComp) : ''
              )
            );
          }
        }
      }
    }

    // Projected moves cross expression-tree storage boundaries, so their
    // prepared map is the only complete graph available to validation.
    let stagedComponents = context.stagedComponents;
    if (!stagedComponents && slotDefaultFieldId) {
      // Stage the defaultData write the drop will commit — the render insertion
      // no-ops on a Select-bound Slot, hiding the new component edge from the
      // circular-reference gate.
      let mapForStaging = liveComponents;
      if (!isNewNode) {
        mapForStaging = updateOneOfComponents(
          rawChild,
          rootElement,
          liveComponents,
          (renderExpr) => removeElementById(renderExpr, rawChild.id)
        );
      }
      stagedComponents = addElementToSlotDefault(
        mapForStaging,
        anchorOwner.componentType,
        slotDefaultFieldId,
        rawChild,
        anchorPosition
      );
    } else if (!stagedComponents) {
      stagedComponents = getStagedComponents(rawChild)(anchorElement)(
        anchorPosition
      )(liveComponents)(rootElement)(anchorPath ? anchorPath : emptyArray)(
        isNewNode
      );
    }

    return validateChanges(
      liveComponentMap,
      stagedComponents,
      validationContext,
      DesignerErrorCodes.getMoveMessage
    ).map(constant(rawChild));
  };

type AddElementDetails = {
  element: ExpressionElement;
  anchorId: string;
  anchorPath?: ExpressionPath | null;
  position: RelativePositionPlacementOptions;
  styleBlockState: StyleBlockStateType;
};

/**
 * A drop that lands inside one of the edited definition's own Slots authors
 * that slot's default in component.defaultData instead of
 * a render child. Returns the field to write, or null for every normal drop.
 */
export const resolveSlotDefaultDropFieldId = ({
  component,
  componentType,
  anchorId,
  position,
  anchorPath,
  componentMap,
  plugins,
}: {
  component: Component.ComponentType | null;
  componentType: Plugins.QualifiedElement;
  anchorId: string;
  position: RelativePositionPlacementOptions;
  anchorPath: ExpressionPath | null;
  componentMap?: Component.ComponentMap;
  plugins?: Plugins.PluginsType;
}): string | null => {
  if (!getFeatureFlagOutsideOfReact(FEATURE_FLAGS.COMPONENT_SLOT_DEFAULTS)) {
    return null;
  }
  // A non-null anchorPath targets a focused instance's slot override, which
  // already has its own write path.
  if (anchorPath || !component || !isUserComponent(componentType)) {
    return null;
  }
  const renderFieldId = getSlotDefaultDropFieldId(
    component,
    anchorId,
    position
  );
  if (renderFieldId) {
    return renderFieldId;
  }

  // The anchor can be an existing default rather than the Slot, and a default is
  // not in the render for the search above to reach. The field holding it is the
  // drop target.
  if (!componentMap || !plugins) {
    return null;
  }
  return (
    findSlotDefaultElement(plugins, componentMap, componentType, anchorId)
      ?.slotFieldId ?? null
  );
};

/**
 * Adds a dropped element to a SlotContent field's component.defaultData.
 * Returns the map unchanged when the field can't be resolved — a slot-default
 * drop must never fall through to a render write.
 */
export const addElementToSlotDefault = (
  componentMap: Component.ComponentMap,
  componentType: Plugins.QualifiedElement,
  fieldId: string,
  element: ExpressionElement,
  position: RelativePositionPlacementOptions
): Component.ComponentMap => {
  const component = componentMap.get(componentType);
  if (!component) {
    return componentMap;
  }
  const dataType = Component.getDataType(component);
  const fields = Types.getRecordFields(dataType) || {};
  const field = fields[fieldId];
  if (!field) {
    return componentMap;
  }

  // Slot defaults only ever land in defaultData, so a component that has not
  // been migrated has nowhere to store one. canAuthorSlotDefault keeps the drop
  // from being offered; this is the backstop.
  const defaultData = Component.getDefaultData(component);
  if (!defaultData) {
    return componentMap;
  }

  const elementExpr = EElement(element);
  const currentDefaultExpr = getSlotDefaultExpression(component, fieldId);
  const currentListExprs: Expression[] = currentDefaultExpr
    ? (Expressions.getList(currentDefaultExpr) ?? [])
    : [];

  const nextListExpr = EList(
    position === 'prepend' || position === 'before'
      ? [elementExpr, ...currentListExprs]
      : [...currentListExprs, elementExpr]
  );

  const nextDefaultData = setSlotDefaultExpression(
    defaultData,
    fieldId,
    nextListExpr
  );

  // A component authored as this Slot's default content must also be allowed in
  // it. Only extend an existing restriction -- an unrestricted Slot stays
  // unrestricted.
  const render = Component.getRender(component);
  const slot = getComponentSlots(component).find(
    (candidate) => getSlotOverrideFieldId(candidate) === fieldId
  );
  let nextRender = render;
  if (slot && isUserComponentInst(componentMap, element)) {
    const allowedComponentIds = getSlotAllowedComponentIds(slot);
    const componentId = `${element.type[0]}::${element.type[1]}`;
    if (
      allowedComponentIds.length > 0 &&
      !allowedComponentIds.includes(componentId)
    ) {
      nextRender = Expressions.updateElement(
        slot.id,
        (slotElement) =>
          setSlotAllowedComponentIds(slotElement, [
            ...allowedComponentIds,
            componentId,
          ]),
        render
      );
    }
  }

  return componentMap.set(
    componentType,
    Component.update(component, {
      defaultData: nextDefaultData,
      render: nextRender,
    })
  );
};

/**
 * True when a drop at this anchor authors a slot default instead of a render
 * child. The grid drag previews must not stage that write: the final commit
 * can't see defaultData content (validateElementAdd searches renders only)
 * and would apply it a second time.
 */
export const isSlotDefaultDrop = ({
  designerState,
  uiNodeState,
  anchorId,
  position,
}: {
  designerState: DesignerStateType;
  uiNodeState: UiNodeStateType;
  anchorId: string;
  position: RelativePositionPlacementOptions;
}): boolean => {
  const anchorOwner = resolveAnchorOwner({
    componentMap: getLiveComponentMap(designerState),
    designerState,
    uiNodeState,
    anchorId,
  });
  return (
    resolveSlotDefaultDropFieldId({
      component: anchorOwner.component,
      componentType: anchorOwner.componentType,
      anchorId,
      position,
      // Deliberately without a component map or plugins, which keeps this to
      // the render lookup. Projected placement asks this only about a drop onto
      // the definition's Slot itself, and resolving a default anchor here would
      // claim drops beside a default that it routes through promotion.
      anchorPath: null,
    }) != null
  );
};

export const addElement =
  (details: AddElementDetails) =>
  (
    designerState: DesignerStateType,
    uiNodeState: UiNodeStateType
  ): Option<Component.ComponentMap> => {
    const componentMap = getLiveComponentMap(designerState);
    const plugins = getPluginsFromState({
      DesignerStore: designerState,
    });
    const anchorOwner = resolveAnchorOwner({
      componentMap,
      designerState,
      uiNodeState,
      anchorId: details.anchorId,
    });

    const validatedAdd = okToOption(
      validateElementAdd({
        element: details.element,
        anchorId: details.anchorId,
        componentMap,
        focusedSymbolNativeIdPath: uiNodeState.focusedSymbolNativeIdPath,
      })
    );

    const slotDefaultFieldId = resolveSlotDefaultDropFieldId({
      component: anchorOwner.component,
      componentType: anchorOwner.componentType,
      anchorId: details.anchorId,
      position: details.position,
      anchorPath: details.anchorPath ?? null,
      componentMap,
      plugins,
    });
    if (slotDefaultFieldId) {
      return validatedAdd.map(() =>
        addElementToSlotDefault(
          componentMap,
          anchorOwner.componentType,
          slotDefaultFieldId,
          details.element,
          details.position
        )
      );
    }

    return validatedAdd
      .map(() => {
        const {element, anchorId, anchorPath, position} = details;
        const anchorElement = Expressions.getElementById(
          anchorOwner.render,
          anchorId
        );

        if (anchorElement) {
          const nextRender = addElementAtAnchorInPath(
            anchorOwner.render,
            element,
            anchorElement,
            position,
            anchorPath ?? null,
            designerState.components
          );
          return nextRender;
        }
        return anchorOwner.render;
      })
      .chain((newRender) => {
        return (
          maybeGetParent(details.element.id)(newRender)
            // An element addition could be via copy/paste, and that pasted element might have float styles
            // that might toggle the data.clearfix on the new parent
            .map((parent) => {
              const newComponentMap = updateComponentRenderInComponentMap(
                componentMap,
                anchorOwner.componentType,
                newRender
              );

              return updateClearfixForElement(
                plugins,
                newComponentMap,
                details.styleBlockState,
                [parent.id]
              );
            })
        );
      })
      .map((updatedComponentMap) =>
        regenerateSlotOverrides(
          details.element,
          anchorOwner.componentType,
          updatedComponentMap
        )
      );
  };

/**
 * Tells the mover the source or the anchor is not where it would normally look.
 * Only Slot-default placements pass it. Leave it off and the move reads and writes
 * the page render as usual.
 *
 * `detached` = promotion already pulled the source out. `new` = it never existed.
 *
 * The anchor is a `root` when it sits in another component's render, or an
 * `expression` when it is a Slot default that has to be written back through its
 * own field. Never both, so the two are one choice.
 */
type ElementMoveOverrides = {
  source?:
    | {kind: 'detached'; element: ExpressionElement}
    | {kind: 'new'; element: ExpressionElement};
  anchor?:
    | {kind: 'root'; root: ExpressionElement}
    | {
        kind: 'expression';
        expression: Expression;
        // Where in that expression the element goes. The promotion resolved it
        // while it still knew which tree it was looking at, so the write places
        // the element instead of searching for an anchor id that a sibling
        // projected tree can answer for just as well.
        insertion: {listPath: ExpressionPath; index: number};
        writeExpression: (
          componentMap: Component.ComponentMap,
          expression: Expression
        ) => Component.ComponentMap;
      };
};

export function handleElementMoved(
  details: {
    id: string;
    anchorId: string;
    position: RelativePositionPlacementOptions;
    anchorPath?: ExpressionPath | null;
  },
  {
    designerState,
    styleBlockState,
    cssGridUiState,
    externalState,
    uiNodeState,
    shift,
  }: {
    designerState: DesignerStateType;
    styleBlockState: StyleBlockStateType;
    cssGridUiState: CssGridUiState;
    externalState: ExternalState;
    uiNodeState: UiNodeStateType;
    shift: boolean;
  },
  moveOverrides?: ElementMoveOverrides
): DesignerStateType {
  // Only Slot-default placements pass overrides, and each one names a way this
  // move deviates from an ordinary one. Read them all here so the body below works
  // from names instead of re-deriving intent from the bag.

  // The source is not in a render to remove from: promotion already pulled it out,
  // or it never existed. An ordinary move always has one.
  let detachedSourceElement: ExpressionElement | null = null;
  if (
    moveOverrides?.source?.kind === 'detached' ||
    moveOverrides?.source?.kind === 'new'
  ) {
    detachedSourceElement = moveOverrides.source.element;
  }

  const skipsEmptySlotReset = Boolean(moveOverrides?.source);

  // Where the anchor is written. An ordinary move writes into the render that holds
  // it; a Slot default is written back through its own field.
  const anchorOverride = moveOverrides?.anchor ?? null;

  // Validation, the anchor lookup and the write all have to search the same
  // tree, or a promoted anchor validates against one root and resolves in another.
  let anchorSearchExpression: Expression | null = null;
  if (anchorOverride?.kind === 'expression') {
    anchorSearchExpression = anchorOverride.expression;
  } else if (anchorOverride?.kind === 'root') {
    anchorSearchExpression = EElement(anchorOverride.root);
  }

  // A detached source sits in no render, so a move check would always fail on a
  // missing node. It is an add as far as validation is concerned, judged against
  // the tree the anchor was resolved in.
  let validation: Result<string, true>;
  if (detachedSourceElement) {
    validation = validateElementAdd({
      element: detachedSourceElement,
      anchorId: details.anchorId,
      anchorRoot: anchorSearchExpression ?? undefined,
      componentMap: getLiveComponentMap(designerState),
      focusedSymbolNativeIdPath: uiNodeState.focusedSymbolNativeIdPath,
    });
  } else {
    validation = validateElementMove({
      id: details.id,
      anchorId: details.anchorId,
      anchorPath: details.anchorPath,
      position: details.position,
      componentMap: getLiveComponentMap(designerState),
      focusedSymbolNativeIdPath: uiNodeState.focusedSymbolNativeIdPath,
    });
  }

  return either(constant(designerState))(() => {
    const {id, anchorId, position, anchorPath} = details;

    const root = selectRootElementWithFocusedInstancesExpanded({
      DesignerStore: designerState,
      UiNodeStore: uiNodeState,
    });
    if (!root) {
      return designerState;
    }
    const oldPath = detachedSourceElement
      ? null
      : getKeyPathToElement(root, id);
    const rootElement = Expressions.getElement(root);
    if (!rootElement || (!oldPath && !detachedSourceElement)) {
      return designerState;
    }
    const data = Expressions.getElementData(rootElement);
    const expressionElement = oldPath
      ? Expressions.getIn(data, oldPath.slice(1))
      : null;
    const element =
      detachedSourceElement ||
      (expressionElement ? Expressions.getElement(expressionElement) : null);
    if (!element) {
      return designerState;
    }

    const anchorElement = Expressions.getElementById(
      anchorSearchExpression ?? root,
      anchorId
    );
    if (!anchorElement) {
      return designerState;
    }

    const componentMap = getLiveComponentMap(designerState);
    const currentComponent = selectCurrentComponent({
      DesignerStore: designerState,
      UiNodeStore: uiNodeState,
    });
    let updatedComponentMap = componentMap;
    if (!skipsEmptySlotReset) {
      updatedComponentMap = resetSlotIfEmpty(
        element,
        currentComponent,
        componentMap,
        rootElement,
        designerState.plugins
      );
    }

    let componentMapWithElementRemoved = updatedComponentMap;
    if (!detachedSourceElement) {
      componentMapWithElementRemoved = updateOneOfComponents(
        element,
        rootElement,
        updatedComponentMap,
        (renderExpr) => removeElementById(renderExpr, element.id)
      );
    }

    // A definition-slot drop authors the slot's default: pull the element out
    // of the render and append it to the field's defaultData in one update.
    const anchorOwner = resolveAnchorOwner({
      componentMap,
      designerState,
      uiNodeState,
      anchorId,
    });
    const slotDefaultFieldId = resolveSlotDefaultDropFieldId({
      component: anchorOwner.component,
      componentType: anchorOwner.componentType,
      anchorId,
      position,
      anchorPath: anchorPath ?? null,
      componentMap,
      plugins: designerState.plugins,
    });
    if (slotDefaultFieldId) {
      // The default's parent-of-record is field data, never a grid — clear
      // grid placement the way placeGridItem would for a non-grid parent.
      const elementForDefault = clearGridItemStylesAllQueries(
        styleBlockState.getOrderedBreakpointIds()
      )(externalState.variantState ?? null)(element);
      const componentMapWithSlotDefault = addElementToSlotDefault(
        componentMapWithElementRemoved,
        anchorOwner.componentType,
        slotDefaultFieldId,
        elementForDefault,
        position
      );
      const nextState = designerState.set(
        'components',
        componentMapWithSlotDefault
      );
      // Only the old parent can need a clearfix refresh — the destination
      // is the field's defaultData, not a render child.
      const oldParent = Expressions.getParent(root, element.id);
      if (!oldParent) {
        return nextState;
      }
      return nextState.set(
        'components',
        updateClearfixForElement(
          getPluginsFromState({DesignerStore: nextState}),
          componentMapWithSlotDefault,
          styleBlockState,
          [oldParent.id]
        )
      );
    }

    const addToAnchor = (expression: Expression) =>
      addElementAtAnchorInPath(
        expression,
        element,
        anchorElement,
        position,
        anchorPath ?? null,
        componentMap
      );
    let nextComponentMap: Component.ComponentMap;
    if (anchorOverride?.kind === 'expression') {
      nextComponentMap = anchorOverride.writeExpression(
        componentMapWithElementRemoved,
        applySlotDefaultInsertion(
          anchorOverride.expression,
          element,
          anchorOverride.insertion
        )
      );
    } else {
      nextComponentMap = updateOneOfComponents(
        anchorElement,
        anchorOverride?.kind === 'root' ? anchorOverride.root : rootElement,
        componentMapWithElementRemoved,
        addToAnchor
      );
    }
    const designerStateAfterMove = designerState.set(
      'components',
      nextComponentMap
    );

    const {pseudoMode, mediaQuery, variantState} = externalState;
    const componentMapAfterGridStyles = placeGridItem({
      id,
      mediaQuery,
      pseudoMode,
      variantState,
      forceManualPlacement: shift,
      cssGridUiState,
      styleBlockState,
      previousDesignerState: designerState,
      nextDesignerState: designerStateAfterMove,
      uiNodeState,
    });

    const stateAfterGridStylesSet = designerStateAfterMove.set(
      'components',
      componentMapAfterGridStyles
    );

    const nextRoot = Component.getRender(
      selectCurrentComponent({
        DesignerStore: stateAfterGridStylesSet,
        UiNodeStore: uiNodeState,
      })
    );

    const oldParent = Expressions.getParent(root, element.id);
    const newParent = Expressions.getParent(nextRoot, id);
    // If an element moved to a different parent, need to make sure to update the clearfix status of each
    // A detached source has no old parent in this render, so only the new one needs refreshing.
    if (newParent && oldParent !== newParent) {
      const pluginsAfterGridStylesSet = getPluginsFromState({
        DesignerStore: stateAfterGridStylesSet,
      });
      return stateAfterGridStylesSet.set(
        'components',
        updateClearfixForElement(
          pluginsAfterGridStylesSet,
          componentMapAfterGridStyles,
          styleBlockState,
          oldParent ? [oldParent.id, newParent.id] : [newParent.id]
        )
      );
    }

    return stateAfterGridStylesSet;
  })(validation);
}

function resetSlotIfEmpty(
  element: ExpressionElement,
  currentComponent: Component.ComponentType,
  componentMap: Component.ComponentMap,
  rootElement: ExpressionElement,
  plugins: Plugins.PluginsType
) {
  const maybeClosestSlot = getClosestSlotInfo(
    element.id,
    currentComponent,
    componentMap
  );
  if (maybeClosestSlot) {
    const {componentInstance, slot} = maybeClosestSlot;
    const updatedComponentInstance = resetSlotPropertyIfEmpty(
      element,
      componentInstance,
      slot,
      componentMap,
      plugins
    );
    return updatedComponentInstance !== componentInstance
      ? updateOneOfComponents(
          element,
          rootElement,
          componentMap,
          (renderExpr) =>
            replaceElement(
              renderExpr,
              componentInstance,
              EElement(updatedComponentInstance)
            )
        )
      : componentMap;
  }
  return componentMap;
}

export function handleElementPresetAdded({
  elementPresetType,
  element,
  pluginName,
  anchorId,
  position,
  initialStyleBlockId,
  assetMap,
  state,
}: {
  elementPresetType: ElementPreset.ElementPresetType;
  element: ExpressionElement;
  pluginName: PluginName;
  anchorId: string;
  position: RelativePositionPlacementOptions;
  initialStyleBlockId: string;
  assetMap: {
    [key: string]: string;
  };
  state: DesignerStores;
}): DesignerStateType {
  const {
    StyleBlockStore: styleBlockState,
    AssetStore: assetState,
    DesignerStore: designerState,
    UiNodeStore: uiNodeState,
    SiteDataStore: siteDataState,
    CssVariablesStore: cssVariablesState,
  } = state;

  const siteId = siteDataState.siteId;

  const elementWithUpdatedAssets = updateElementAssetsFromPreset(
    element,
    siteId,
    assetMap
  );
  const elementWithUpdatedStylesAndAssets = Expressions.getElement(
    updateXscpStyleBlockIds(
      importNodeAddedStylesFromElementPreset({
        elementPresetType,
        pluginName,
        initialStyleBlockId,
        styleBlockState,
        assets: assetState.assets,
        variables: cssVariablesState.variables,
        variableCollections: cssVariablesState.variableCollections,
        plugins: designerState.plugins,
      }).idMap
    )(EElement(elementWithUpdatedAssets))
  );

  if (!elementWithUpdatedStylesAndAssets) return designerState;

  const root = selectRootElementWithFocusedInstancesExpanded({
    DesignerStore: designerState,
    UiNodeStore: uiNodeState,
  });
  const rootElement = root ? Expressions.getElement(root) : null;

  if (!root || !rootElement) {
    return designerState;
  }

  const componentMap = getLiveComponentMap(designerState);
  const componentMapWithElementRemoved = updateOneOfComponents(
    element,
    rootElement,
    componentMap,
    (renderExpr) => removeElementById(renderExpr, element.id)
  );
  const anchorElement = Expressions.getElementById(root, anchorId);

  if (!anchorElement) {
    return designerState;
  }

  const nextComponentMap = updateOneOfComponents(
    anchorElement,
    rootElement,
    componentMapWithElementRemoved,
    (renderExpr) =>
      addElementAtAnchor(
        renderExpr,
        elementWithUpdatedStylesAndAssets,
        anchorElement,
        position
      )
  );

  const designerStateAfterAddingElementPreset = designerState.set(
    'components',
    nextComponentMap
  );

  return designerStateAfterAddingElementPreset;
}

// Retrieves the element that is actively being dragged.
export function getDragElement(
  dragDropState: DragDropState,
  root: Expression,
  designerState: DesignerStateType
): ExpressionElement | null {
  const {dragNodeId, dragSource} = dragDropState;
  const dragElementExpression = getElementExprByIdOrInterimDragElement(
    root,
    dragDropState,
    dragNodeId
  );
  if (dragElementExpression) {
    return Expressions.getElement(dragElementExpression);
  }

  return SlotDefaults.resolveProjectedSourceElement({
    designerState,
    dragSource,
  });
}

/**
 * Retrieves the element that is the proposed drop target for the dragged element.
 *   - If the hovered element is a typographic or rich text element,
 *     we return the first non-typographic ancestor.
 *   - If the element is in a user component, we return the user component instance.
 *   - If the user is in Page Build Mode, they can only drag to a page slot or rich text.
 */
function getProposedAnchorElement(
  dragDropState: DragDropState,
  root: Expression,
  hoveredNativeIdPath: NativeId[],
  focusedSymbolNativeIdPath: NativeId[],
  isInPageBuildMode: boolean,
  designerState: DesignerStateType,
  componentMap: Component.ComponentMap,
  canAddComponentsInStaticRichText: boolean
): ExpressionElement | null {
  const hoveredElementNativeId = deriveNativeIdFromPath(
    hoveredNativeIdPath,
    focusedSymbolNativeIdPath
  );
  if (!hoveredElementNativeId) return null;

  let initialHoveredElement = resolveDragDropElement({
    root,
    nativeIdPath: hoveredNativeIdPath,
    focusedSymbolNativeIdPath,
    designerState,
  });
  if (!initialHoveredElement) {
    const hoveredElementExpr = getElementExprByIdOrInterimDragElement(
      root,
      dragDropState,
      hoveredElementNativeId
    );
    if (hoveredElementExpr) {
      initialHoveredElement = Expressions.getElement(hoveredElementExpr);
    }
  }
  if (!initialHoveredElement) return null;

  // Typographic and RichText nodes can't be dragged into, so proxy to the parent node
  const furthestTextNode = furthestTextHolderElement(root)(
    initialHoveredElement
  );

  if (isInPageBuildMode) {
    const isHoveredElementAPageSlot = isComponentOnlyDropTarget(
      initialHoveredElement
    );

    // If the hovered element is a page slot, it is valid and we don't need to check further.
    let isValidPageBuildModeAnchorElement = isHoveredElementAPageSlot;

    if (!isValidPageBuildModeAnchorElement) {
      if (canAddComponentsInStaticRichText) {
        const isHoveredElementRichText = isRichText(initialHoveredElement);

        if (isHoveredElementRichText) {
          isValidPageBuildModeAnchorElement = true;
        } else {
          // Users in page build mode can hover over elements on the canvas that are editable,
          // like text, links, images, etc., but they can only drop into page slots or rich text.
          const isDescendantOfPageSlot =
            getIsComponentOnlyPageSlotDescendantFromPath(
              hoveredNativeIdPath,
              componentMap
            );
          const isDescendantOfRichText = getIsRichTextDescendantFromPath(
            hoveredNativeIdPath,
            componentMap
          );

          isValidPageBuildModeAnchorElement =
            isDescendantOfPageSlot || isDescendantOfRichText;
        }
      } else {
        // Users in page build mode can hover over elements on the canvas that are editable, like text, links, images, etc., but they can only drop into page slots
        const isDescendantOfPageSlot =
          getIsComponentOnlyPageSlotDescendantFromPath(
            hoveredNativeIdPath,
            componentMap
          );

        isValidPageBuildModeAnchorElement = isDescendantOfPageSlot;
      }
    }

    if (!isValidPageBuildModeAnchorElement) return null;
  }

  const proposedAnchorElement = furthestTextNode
    ? furthestTextNode
    : initialHoveredElement;

  return proposedAnchorElement;
}

export function getInitialDragAndDropElements(
  designerState: DesignerStateType,
  uiNodeState: UiNodeStateType,
  dragDropState: DragDropState,
  hoveredNativeIdPath: NativeId[],
  isInPageBuildMode: boolean,
  canAddComponentsInStaticRichText: boolean
): null | {
  proposedAnchorElement: ExpressionElement;
  dragElement: ExpressionElement;
  root: Expression;
} {
  const root = selectRootElementWithFocusedInstancesExpanded({
    DesignerStore: designerState,
    UiNodeStore: uiNodeState,
  });
  if (!root) return null;

  const componentMap = getLiveComponentMap(designerState);
  const proposedAnchorElement = getProposedAnchorElement(
    dragDropState,
    root,
    hoveredNativeIdPath,
    uiNodeState.focusedSymbolNativeIdPath,
    isInPageBuildMode,
    designerState,
    componentMap,
    canAddComponentsInStaticRichText
  );
  if (!proposedAnchorElement) return null;

  const dragElement = getDragElement(dragDropState, root, designerState);
  if (!dragElement) return null;

  return {
    proposedAnchorElement,
    dragElement,
    root,
  };
}

export const isDroppingIntoGridParentCell = ({
  dropParent,
  externalState,
  styleBlockState,
  cssGridUiState,
}: {
  dropParent: ExpressionElement;
  externalState: ExternalState;
  styleBlockState: StyleBlockStateType;
  cssGridUiState: CssGridUiState;
}) =>
  (cssGridUiState.hoveredCell || cssGridUiState.hoveredAreaName) &&
  isElementGridDisplay(
    dropParent,
    externalState.mediaQuery,
    externalState.pseudoMode,
    externalState.variantState,
    styleBlockState.cssPropertyMap
  );

/**
 * Function for building the native id path for a given element being dragged in which
 * `elementOrComponentInstanceId` and `maybeComponentRootId` are appended with instance suffixes, if present.
 */
export const buildDragElementNativeIdPath = (
  focusedSymbolNativeIdPath: NativeId[],
  elementOrComponentInstanceId: string,
  maybeComponentRootId?: string
): NativeId[] => {
  const innermostComponentInstanceNativeId =
    focusedSymbolNativeIdPath[focusedSymbolNativeIdPath.length - 1];
  const instanceIndices = innermostComponentInstanceNativeId
    ? getInstanceIndicesFromNativeId(innermostComponentInstanceNativeId)
    : [];

  const elementOrComponentInstanceNativeId = instanceIndices.length
    ? generateInstanceNativeId(elementOrComponentInstanceId, instanceIndices)
    : convertElementIdToNativeId(elementOrComponentInstanceId);

  if (!maybeComponentRootId) {
    return [...focusedSymbolNativeIdPath, elementOrComponentInstanceNativeId];
  }

  const componentRootNativeId = instanceIndices.length
    ? generateInstanceNativeId(maybeComponentRootId, instanceIndices)
    : convertElementIdToNativeId(maybeComponentRootId);

  return [
    ...focusedSymbolNativeIdPath,
    elementOrComponentInstanceNativeId,
    componentRootNativeId,
  ];
};
